import {
  AddOutlined,
  ArchiveOutlined,
  BusinessOutlined,
  EditOutlined,
  LocationOnOutlined,
  SearchOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DeliveryClient,
  DeliveryDestination,
  archiveDeliveryClient,
  archiveDeliveryDestination,
  createDeliveryClient,
  createDeliveryDestination,
  loadDeliveryClients,
  loadProcessPointMasterData,
  updateDeliveryClient,
  updateDeliveryDestination
} from "./api";
import { ListRowActions } from "./ListRowActions";
import { PageHeader } from "./PageHeader";

const organizationId = "10000000-0000-0000-0000-000000000001";
type DestinationType = "POSTAL_ADDRESS" | "POU";

const emptyDestination = {
  code: "", displayName: "", type: "POU" as DestinationType,
  processPointId: "", addressLine1: "", addressLine2: "",
  postalCode: "", city: "", region: "", countryCode: "FR",
  contactName: "", contactPhone: "", siteName: "", building: "",
  assemblyLine: "", workArea: "", scanCode: "", instructions: ""
};

export function ClientDestinationMasterData({
  onBack, permissions
}: {
  onBack: () => void;
  permissions: string[];
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const canManageClients = permissions.includes("client:manage");
  const canManageDestinations =
    permissions.includes("delivery_destination:manage");
  const [tab, setTab] = useState<"active" | "archived">("active");
  const [search, setSearch] = useState("");
  const [clientDialog, setClientDialog] =
    useState<{ client?: DeliveryClient } | null>(null);
  const [clientCode, setClientCode] = useState("");
  const [clientName, setClientName] = useState("");
  const [reason, setReason] = useState("");
  const [destinationDialog, setDestinationDialog] = useState<{
    client: DeliveryClient; destination?: DeliveryDestination;
  } | null>(null);
  const [destination, setDestination] = useState(emptyDestination);
  const [archiveTarget, setArchiveTarget] = useState<{
    type: "client" | "destination";
    client?: DeliveryClient;
    destination?: DeliveryDestination;
  } | null>(null);

  const clients = useQuery({
    queryKey: ["delivery-clients"], queryFn: loadDeliveryClients
  });
  const processPoints = useQuery({
    queryKey: ["process-point-master-data"],
    queryFn: loadProcessPointMasterData
  });
  const pouPoints = (processPoints.data ?? []).filter(
    (point) => point.status === "ACTIVE"
      && point.capabilities.includes("POU_DELIVERY")
  );
  const filtered = useMemo(() => (clients.data ?? []).filter((entry) => {
    const active = entry.status === "ACTIVE";
    const needle = search.trim().toLocaleLowerCase("fr");
    const haystack = `${entry.code} ${entry.displayName} ${
      entry.destinations.map((item) =>
        `${item.code} ${item.displayName} ${item.city ?? ""} ${
          item.siteName ?? ""}`).join(" ")
    }`.toLocaleLowerCase("fr");
    return active === (tab === "active") && (!needle || haystack.includes(needle));
  }), [clients.data, search, tab]);

  const refresh = () =>
    client.invalidateQueries({ queryKey: ["delivery-clients"] });
  const saveClient = useMutation({
    mutationFn: () => clientDialog?.client
      ? updateDeliveryClient(clientDialog.client.id, {
          displayName: clientName, reason,
          version: clientDialog.client.version
        })
      : createDeliveryClient({
          organizationId, code: clientCode, displayName: clientName
        }),
    onSuccess: async () => {
      setClientDialog(null); setReason(""); await refresh();
    }
  });
  const saveDestination = useMutation({
    mutationFn: () => {
      const current = destinationDialog?.destination;
      const payload = {
        clientId: destinationDialog!.client.id,
        ...destination,
        processPointId: destination.processPointId || null,
        reason: current ? reason : null,
        version: current?.version ?? null
      };
      return current
        ? updateDeliveryDestination(current.id, payload)
        : createDeliveryDestination(payload);
    },
    onSuccess: async () => {
      setDestinationDialog(null); setReason(""); await refresh();
    }
  });
  const archive = useMutation({
    mutationFn: async () => {
      if (archiveTarget?.type === "client") {
        await archiveDeliveryClient(
          archiveTarget.client!.id, archiveTarget.client!.version, reason
        );
      } else {
        await archiveDeliveryDestination(
          archiveTarget!.destination!.id,
          archiveTarget!.destination!.version, reason
        );
      }
    },
    onSuccess: async () => {
      setArchiveTarget(null); setReason(""); await refresh();
    }
  });

  function openClient(value?: DeliveryClient) {
    setClientCode(value?.code ?? "");
    setClientName(value?.displayName ?? "");
    setReason("");
    setClientDialog({ client: value });
  }
  function openDestination(
    owner: DeliveryClient, value?: DeliveryDestination
  ) {
    setDestination({
      code: value?.code ?? "",
      displayName: value?.displayName ?? "",
      type: value?.type ?? "POU",
      processPointId: value?.processPointId ?? "",
      addressLine1: value?.addressLine1 ?? "",
      addressLine2: value?.addressLine2 ?? "",
      postalCode: value?.postalCode ?? "",
      city: value?.city ?? "",
      region: value?.region ?? "",
      countryCode: value?.countryCode ?? "FR",
      contactName: value?.contactName ?? "",
      contactPhone: value?.contactPhone ?? "",
      siteName: value?.siteName ?? "",
      building: value?.building ?? "",
      assemblyLine: value?.assemblyLine ?? "",
      workArea: value?.workArea ?? "",
      scanCode: value?.scanCode ?? "",
      instructions: value?.instructions ?? ""
    });
    setReason("");
    setDestinationDialog({ client: owner, destination: value });
  }
  const setField = (field: keyof typeof emptyDestination, value: string) =>
    setDestination((current) => ({ ...current, [field]: value }));

  return (
    <Stack spacing={1.5} sx={{
      height: { md: "100%" }, minHeight: 0
    }}>
      <PageHeader compact title={t("masterData.clients.title")} onBack={onBack}
        actions={canManageClients ? (
          <Button variant="contained" startIcon={<AddOutlined />}
            onClick={() => openClient()}>
            {t("masterData.clients.new")}
          </Button>
        ) : undefined} />
      <Card>
        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
          <TextField fullWidth size="small" label={t("masterData.common.search")}
            placeholder={t("masterData.clients.searchPlaceholder")}
            value={search} onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment:
              <SearchOutlined color="action" sx={{ mr: 1 }} /> }} />
        </CardContent>
      </Card>
      <Tabs value={tab}
        onChange={(_, value: "active" | "archived") => setTab(value)}>
        <Tab value="active" label={`${t("masterData.common.active")} (${(clients.data ?? []).filter(
          (entry) => entry.status === "ACTIVE").length})`} />
        <Tab value="archived" label={`${t("masterData.common.archived")} (${(clients.data ?? []).filter(
          (entry) => entry.status !== "ACTIVE").length})`} />
      </Tabs>
      <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", pr: .5 }}>
        <Stack spacing={1}>
          {clients.isError && (
            <Alert severity="error">{t("masterData.clients.loadError")}</Alert>
          )}
          {!clients.isLoading && filtered.length === 0 && (
            <Alert severity="info">{t("masterData.clients.empty")}</Alert>
          )}
          {filtered.map((entry) => (
            <Card key={entry.id}>
              <CardContent>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                  alignItems={{ md: "center" }}>
                  <BusinessOutlined color="secondary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography fontWeight={800}>
                      {entry.code} · {entry.displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {entry.destinations.filter(
                        (item) => item.status === "ACTIVE").length
                      } destination(s) active(s)
                    </Typography>
                  </Box>
                  <ListRowActions actions={
                    entry.status === "ACTIVE" ? (
                      <Stack direction="row" spacing={1}>
                        {canManageDestinations && (
                          <Button variant="outlined"
                            startIcon={<LocationOnOutlined />}
                            onClick={() => openDestination(entry)}>
                            {t("masterData.clients.createDestination")}
                          </Button>
                        )}
                        {canManageClients && (
                          <>
                            <Button variant="outlined"
                              startIcon={<EditOutlined />}
                              onClick={() => openClient(entry)}>
                              {t("masterData.common.edit")}
                            </Button>
                            <Button variant="outlined" color="error"
                              startIcon={<ArchiveOutlined />}
                              onClick={() => {
                                setReason("");
                                setArchiveTarget({ type: "client", client: entry });
                              }}>
                              {t("masterData.common.archive")}
                            </Button>
                          </>
                        )}
                      </Stack>
                    ) : undefined
                  } status={<Chip label={t(`masterData.common.statuses.${entry.status}`, { defaultValue: entry.status })}
                    color={entry.status === "ACTIVE"
                      ? "success" : "default"} />} />
                </Stack>
                {entry.destinations.length > 0 && (
                  <Stack spacing={.75} sx={{ mt: 1.5 }}>
                    <Divider />
                    {entry.destinations.map((item) => (
                      <Stack key={item.id}
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5} alignItems={{ md: "center" }}
                        sx={{ py: .75 }}>
                        <LocationOnOutlined fontSize="small" color="action" />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight={750}>
                            {item.code} · {item.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.type === "POU"
                              ? `POU · ${item.siteName}${
                                item.assemblyLine ? ` · ${item.assemblyLine}` : ""
                              } · scan ${item.scanCode}`
                              : `${item.addressLine1} · ${item.postalCode} ${
                                item.city} · ${item.countryCode}`}
                          </Typography>
                        </Box>
                        <ListRowActions actions={
                          canManageDestinations && item.status === "ACTIVE" ? (
                            <Stack direction="row" spacing={1}>
                              <Button size="small" variant="outlined"
                                onClick={() => openDestination(entry, item)}>
                                {t("masterData.common.edit")}
                              </Button>
                              <Button size="small" variant="outlined" color="error"
                                onClick={() => {
                                  setReason("");
                                  setArchiveTarget({
                                    type: "destination", destination: item
                                  });
                                }}>
                                {t("masterData.common.archive")}
                              </Button>
                            </Stack>
                          ) : undefined
                        } status={<Chip size="small" label={t(`masterData.common.statuses.${item.status}`, { defaultValue: item.status })}
                          color={item.status === "ACTIVE"
                            ? "success" : "default"} />} />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>

      <Dialog open={clientDialog !== null}
        onClose={() => setClientDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {clientDialog?.client ? t("masterData.clients.editClient") : t("masterData.clients.newClient")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label={t("masterData.clients.clientCode")} required value={clientCode}
              disabled={Boolean(clientDialog?.client)}
              onChange={(event) => setClientCode(
                event.target.value.toUpperCase()
              )} />
            <TextField label={t("masterData.common.name")} required value={clientName}
              onChange={(event) => setClientName(event.target.value)} />
            {clientDialog?.client && (
              <TextField label={t("masterData.common.reason")} required
                value={reason}
                onChange={(event) => setReason(event.target.value)} />
            )}
            {saveClient.isError && (
              <Alert severity="error">
                {saveClient.error instanceof Error
                  ? saveClient.error.message : t("masterData.common.saveError")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientDialog(null)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained"
            disabled={!clientName.trim() || (!clientDialog?.client
              && !clientCode.trim()) || (Boolean(clientDialog?.client)
              && reason.trim().length < 3)}
            onClick={() => saveClient.mutate()}>
            {t("masterData.common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={destinationDialog !== null}
        onClose={() => setDestinationDialog(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {destinationDialog?.destination ? t("masterData.clients.editDestination") : t("masterData.clients.createDestination")}
          {" · "}{destinationDialog?.client.code}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField fullWidth required label={t("masterData.clients.destinationCode")}
                disabled={Boolean(destinationDialog?.destination)}
                value={destination.code}
                onChange={(event) => setField(
                  "code", event.target.value.toUpperCase()
                )} />
              <TextField fullWidth required label={t("masterData.common.name")}
                value={destination.displayName}
                onChange={(event) =>
                  setField("displayName", event.target.value)} />
              <TextField select fullWidth label={t("masterData.clients.destinationType")}
                value={destination.type}
                onChange={(event) =>
                  setField("type", event.target.value)}>
                <MenuItem value="POU">{t("masterData.clients.pointOfUse")}</MenuItem>
                <MenuItem value="POSTAL_ADDRESS">{t("masterData.clients.postalAddress")}</MenuItem>
              </TextField>
            </Stack>
            {destination.type === "POU" ? (
              <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField fullWidth required label={t("masterData.clients.site")}
                    value={destination.siteName}
                    onChange={(event) =>
                      setField("siteName", event.target.value)} />
                  <TextField fullWidth label={t("masterData.clients.building")}
                    value={destination.building}
                    onChange={(event) =>
                      setField("building", event.target.value)} />
                  <TextField fullWidth label={t("masterData.clients.assemblyLine")}
                    value={destination.assemblyLine}
                    onChange={(event) =>
                      setField("assemblyLine", event.target.value)} />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField fullWidth label={t("masterData.clients.area")}
                    value={destination.workArea}
                    onChange={(event) =>
                      setField("workArea", event.target.value)} />
                  <TextField fullWidth required label={t("masterData.clients.scanCode")}
                    value={destination.scanCode}
                    onChange={(event) =>
                      setField("scanCode", event.target.value)} />
                  <TextField select fullWidth label={t("masterData.clients.station")}
                    value={destination.processPointId}
                    onChange={(event) =>
                      setField("processPointId", event.target.value)}>
                    <MenuItem value="">{t("masterData.common.none")}</MenuItem>
                    {pouPoints.map((point) => (
                      <MenuItem key={point.id} value={point.id}>
                        {point.warehouseCode} · {point.code} · {point.displayName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </>
            ) : (
              <>
                <TextField required label={t("masterData.clients.address")}
                  value={destination.addressLine1}
                  onChange={(event) =>
                    setField("addressLine1", event.target.value)} />
                <TextField label={t("masterData.clients.addressLine2")}
                  value={destination.addressLine2}
                  onChange={(event) =>
                    setField("addressLine2", event.target.value)} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField fullWidth required label={t("masterData.clients.postalCode")}
                    value={destination.postalCode}
                    onChange={(event) =>
                      setField("postalCode", event.target.value)} />
                  <TextField fullWidth required label={t("masterData.clients.city")}
                    value={destination.city}
                    onChange={(event) =>
                      setField("city", event.target.value)} />
                  <TextField fullWidth required label={t("masterData.clients.country")}
                    value={destination.countryCode}
                    onChange={(event) => setField(
                      "countryCode", event.target.value.toUpperCase()
                    )} inputProps={{ maxLength: 2 }} />
                </Stack>
              </>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField fullWidth label={t("masterData.clients.contact")}
                value={destination.contactName}
                onChange={(event) =>
                  setField("contactName", event.target.value)} />
              <TextField fullWidth label={t("masterData.clients.phone")}
                value={destination.contactPhone}
                onChange={(event) =>
                  setField("contactPhone", event.target.value)} />
            </Stack>
            <TextField multiline minRows={2} label={t("masterData.clients.instructions")}
              value={destination.instructions}
              onChange={(event) =>
                setField("instructions", event.target.value)} />
            {destinationDialog?.destination && (
              <TextField required label={t("masterData.common.reason")}
                value={reason}
                onChange={(event) => setReason(event.target.value)} />
            )}
            {saveDestination.isError && (
              <Alert severity="error">
                {saveDestination.error instanceof Error
                  ? saveDestination.error.message
                  : "Enregistrement impossible."}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDestinationDialog(null)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained"
            disabled={!destination.code.trim()
              || !destination.displayName.trim()
              || (destination.type === "POU"
                ? !destination.siteName.trim() || !destination.scanCode.trim()
                : !destination.addressLine1.trim()
                  || !destination.postalCode.trim()
                  || !destination.city.trim()
                  || destination.countryCode.trim().length !== 2)
              || (Boolean(destinationDialog?.destination)
                && reason.trim().length < 3)}
            onClick={() => saveDestination.mutate()}>
            {t("masterData.common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("masterData.common.confirmArchive")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t("masterData.clients.archiveBlocked")}
            </Alert>
            <TextField required multiline minRows={2}
              label={t("masterData.common.archiveReason")} value={reason}
              onChange={(event) => setReason(event.target.value)} />
            {archive.isError && (
              <Alert severity="error">
                {archive.error instanceof Error
                  ? archive.error.message : "Archivage impossible."}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained" color="error"
            disabled={reason.trim().length < 3}
            onClick={() => archive.mutate()}>
            {t("masterData.common.archive")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
